import { StudyGate } from '@@types/api.types';
import { Tile } from '@components/shared/tile.component';
import { Badge } from '@components/ui/badge';
import { Skeleton } from '@components/ui/skeleton';
import { type MeiaStudyGateEntry, useGetMeiaStudyGates } from '@queries/meia.query';
import type { FC } from 'react';

type MeiaGatesTileProps = {
  studyId: string;
};

const gateNames: Record<StudyGate, string> = {
  [StudyGate.G1]: 'Mission Frame Gate (soft)',
  [StudyGate.G2]: 'Thread Design Gate',
  [StudyGate.G3]: 'Scoring Gate',
  [StudyGate.G4]: 'DM Approval Gate',
};

const getGateStatus = (
  entry: MeiaStudyGateEntry,
): { label: string; variant: 'destructive' | 'outline' | 'pending' | 'success' } => {
  if (entry.isError) return { label: 'Unavailable', variant: 'outline' };
  if (!entry.readiness) return { label: 'Pending', variant: 'pending' };
  if (entry.readiness.ready) return { label: 'Ready', variant: 'success' };
  if (entry.readiness.blocking.length > 0) return { label: 'Blocked', variant: 'destructive' };
  return { label: 'Pending', variant: 'pending' };
};

export const MeiaGatesTile: FC<MeiaGatesTileProps> = (props) => {
  const { studyId } = props;
  const { gates } = useGetMeiaStudyGates({ studyId });

  return (
    <Tile header={{ title: 'Gates' }}>
      <ul className="flex flex-col gap-2 text-xs">
        {gates.map((entry) => {
          const status = getGateStatus(entry);

          return (
            <li
              key={entry.gate}
              className="flex items-center gap-2"
            >
              <span className="w-6 font-mono text-gray-500">{entry.gate}</span>
              <span className="flex-1 text-gray-800">{gateNames[entry.gate]}</span>
              {entry.isLoading ? (
                <Skeleton className="h-4 w-14" />
              ) : (
                <Badge
                  size="xs"
                  variant={status.variant}
                >
                  {status.label}
                </Badge>
              )}
            </li>
          );
        })}
      </ul>
    </Tile>
  );
};
